import React, { useState } from "react";
import './Help.css';
import DraggableModal from '../Modal/DraggableModal';

function Help({ displayModal }) {
    const [activeSection, setActiveSection] = useState('basics');

    return (
        <DraggableModal modalId="help" onClose={displayModal}>
            <div className="modal-content" style={{ width: '750px' }}>
                <div className="tmodal-header">
                    <span className="close" onClick={displayModal}>&times;</span>
                    <h2>Help Guide</h2>
                </div>
                <div className="modal-body" style={{ width: '100%', padding: '10px 15px' }}>
                    <div className="help-container">
                        <HelpMenu activeSection={activeSection} setActiveSection={setActiveSection} />
                        <HelpContent activeSection={activeSection} />
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="ts-close" onClick={displayModal}>Close</button>
                </div>
            </div>
        </DraggableModal>
    );
}

function HelpMenu({ activeSection, setActiveSection }) {
    const sections = [
        { id: 'basics', name: 'The Basics' },
        { id: 'movement', name: 'Movement' },
        { id: 'combat', name: 'Combat' },
        { id: 'stats', name: 'Character Stats' },
        { id: 'abilities', name: 'Abilities & Leveling' },
        { id: 'inventory', name: 'Inventory & Equipment' },
        { id: 'communication', name: 'Communication' },
        { id: 'hotkeys', name: 'Hotkeys & Macros' },
        { id: 'interface', name: 'User Interface' },
        { id: 'commands', name: 'Command Reference' },
        { id: 'keyboard', name: 'Keyboard Shortcuts' }
    ];

    return (
        <ul className="help-menu">
            {sections.map((section) => (
                <li 
                    key={section.id}
                    className={activeSection === section.id ? 'active' : ''}
                    onClick={() => setActiveSection(section.id)}
                >
                    {section.name}
                </li>
            ))}
        </ul>
    );
}

function HelpContent({ activeSection }) {
    const content = {
        basics: (
            <div className="help-section">
                <h3>The Basics</h3>
                <p>Welcome to Cursed Lands! This simple guide will help you get started with the game.</p>
                
                <div className="help-card">
                    <h4>Getting Started</h4>
                    <p>Cursed Lands is a multi-user text-based RPG game (MUD) with a custom client. Here's what you need to know to begin your adventure:</p>
                </div>
                
                <h4>Moving Around</h4>
                <ul>
                    <li>Use <span className="key-box">W</span><span className="key-box">A</span><span className="key-box">S</span><span className="key-box">D</span> keys to move in four directions</li>
                    <li>Use <span className="key-box">R</span> and <span className="key-box">F</span> to move up and down</li>
                    <li>Arrow keys <span className="key-box">↑</span><span className="key-box">↓</span><span className="key-box">←</span><span className="key-box">→</span> also work for movement</li>
                    <li>Click on the navigation buttons next to the map to move</li>
                    <li>Make sure the chat input is empty for keyboard movement to work</li>
                </ul>
                
                <h4>Basic Actions</h4>
                <ul>
                    <li>Click on a target in the userlist to select it</li>
                    <li>Press <span className="key-box">F1</span> or click the Attack button to attack</li>
                    <li>Press <span className="key-box">C</span> to focus the chat input</li>
                    <li>Type <span className="command-box">/help</span> to open this guide anytime</li>
                </ul>
                
                <h4>Quick Tips</h4>
                <ul>
                    <li>Look for NPCs with "learn" or "shop" buttons in the userlist</li>
                    <li>Right-click on hotkey buttons to customize them</li>
                    <li>Right-click on players or mobs for additional options</li>
                    <li>Check the other sections of this guide for more detailed information</li>
                </ul>
            </div>
        ),
        
        stats: (
            <div className="help-section">
                <h3>Character Stats</h3>
                <p>Your character has several important stats that affect gameplay and combat effectiveness.</p>
                
                <h4>Primary Stats</h4>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Stat</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Strength</strong></td>
                            <td>Improves your melee damage</td>
                        </tr>
                        <tr>
                            <td><strong>Agility</strong></td>
                            <td>Improves your recovery/cast speed</td>
                        </tr>
                        <tr>
                            <td><strong>Toughness</strong></td>
                            <td>Improves your maximum health and health regeneration</td>
                        </tr>
                        <tr>
                            <td><strong>Wisdom</strong></td>
                            <td>Improves your maximum mana and mana regeneration</td>
                        </tr>
                        <tr>
                            <td><strong>Intelligence</strong></td>
                            <td>Improves spell damage</td>
                        </tr>
                        <tr>
                            <td><strong>Charisma</strong></td>
                            <td>Improves healing & some buffs</td>
                        </tr>
                    </tbody>
                </table>
                
                <h4>Stat Points</h4>
                <p>When you level up, you receive Stat Points that can be used to increase any of your base stats.</p>
                <ul>
                    <li>Click the "Train Stats" button in the action bar to open the stat training window</li>
                    <li>Choose which stat to increase by clicking the "+" button next to it</li>
                    <li>Your available stat points are displayed at the top of the window</li>
                </ul>
            </div>
        ),
        
        abilities: (
            <div className="help-section">
                <h3>Abilities & Leveling</h3>
                <p>As you progress in the game, you'll gain experience points, levels, and new abilities.</p>
                
                <h4>Experience & Leveling</h4>
                <ul>
                    <li>Gain experience by defeating enemies and completing quests</li>
                    <li>When you have enough experience to level up, visit your guild trainer</li>
                    <li>Click the "train" button on your trainer in the userlist</li>
                    <li>As you level up, you will gain stat and ability points</li>
                    <li>Use the <span className="command-box">/tnl</span> command to see how much experience you need to level up</li>
                </ul>
                
                <h4>Learning Abilities</h4>
                <ul>
                    <li>Find your class trainer in a city or guild hall</li>
                    <li>Click on the trainer in the userlist</li>
                    <li>Click the "learn" button that appears</li>
                    <li>In the trainer panel, you can see available abilities and their costs</li>
                    <li>Click on an ability to learn or upgrade it using your ability points</li>
                </ul>
                
                <h4>Using Abilities</h4>
                <ul>
                    <li>Use abilities with the <span className="command-box">/s ability</span> or <span className="command-box">/cast spell</span> commands</li>
                    <li>Target-based abilities use the format: <span className="command-box">/s ability, target</span></li>
                    <li>Set up hotkeys for your frequently used abilities (see Hotkeys section)</li>
                    <li>Some abilities have cooldowns before they can be used again</li>
                    <li>Active buffs and cooldowns are displayed on your screen</li>
                </ul>
                
                <div className="help-card">
                    <h4>Ability Types</h4>
                    <p>Abilities fall into several categories:</p>
                    <ul>
                        <li><strong>Combat Skills</strong> - Physical attacks and maneuvers</li>
                        <li><strong>Spells</strong> - Magical abilities that use mana</li>
                        <li><strong>Passive Abilities</strong> - Always active, providing bonuses</li>
                        <li><strong>Buffs</strong> - Temporary enhancements for you or allies</li>
                    </ul>
                </div>
            </div>
        ),
        
        movement: (
            <div className="help-section">
                <h3>Movement</h3>
                <p>There are multiple ways to move around in the game. Choose the method that works best for you.</p>
                
                <h4>Keyboard Movement</h4>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Key</th>
                            <th>Direction</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><span className="key-box">↑</span> or <span className="key-box">W</span></td>
                            <td>Move North</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">↓</span> or <span className="key-box">S</span></td>
                            <td>Move South</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">→</span> or <span className="key-box">D</span></td>
                            <td>Move East</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">←</span> or <span className="key-box">A</span></td>
                            <td>Move West</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Page Up</span> or <span className="key-box">R</span></td>
                            <td>Move Up/Above</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Page Down</span> or <span className="key-box">F</span></td>
                            <td>Move Down/Below</td>
                        </tr>
                    </tbody>
                </table>
                
                <h4>Mouse Movement</h4>
                <ul>
                    <li>Click on the navigation buttons next to the map</li>
                    <li>The buttons show available exits from your current location</li>
                </ul>
                
                <h4>Movement Tips</h4>
                <ul>
                    <li>The chat input must be empty for keyboard movement keys to work</li>
                    <li>Press <span className="key-box">C</span> to focus the chat input</li>
                    <li>Press <span className="key-box">Escape</span> to unfocus the chat input</li>
                    <li>You can see available exits in the room description</li>
                    <li>Some exits may be hidden or require specific conditions to use</li>
                </ul>
            </div>
        ),
        
        combat: (
            <div className="help-section">
                <h3>Combat</h3>
                <p>Combat is a core part of gameplay. Here's how to engage in combat and use abilities.</p>
                
                <h4>Basic Combat</h4>
                <ul>
                    <li>Click on a target in the userlist to select it</li>
                    <li>Press F1 or click the Attack button to begin attacking</li>
                    <li>Use the <span className="command-box">/stop</span> command or F2 to stop attacking</li>
                </ul>
                
                <h4>Combat Commands</h4>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Command</th>
                            <th>Description</th>
                            <th>Example</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><span className="command-box">/a</span></td>
                            <td>Attack selected target</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/a target</span></td>
                            <td>Attack a specific target</td>
                            <td>/a brawler</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/stop</span></td>
                            <td>Stop attacking/casting</td>
                            <td></td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/s ability</span></td>
                            <td>Use an ability</td>
                            <td>/s kick</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/s ability, target</span></td>
                            <td>Use ability on target</td>
                            <td>/s fireball, 0</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/autoattack</span></td>
                            <td>Toggle auto attack</td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>
                
                <h4>Targeting</h4>
                <p>When targeting, you can use:</p>
                <ul>
                    <li>Full or partial name (e.g., "brawler" or "bra")</li>
                    <li>Position in room list (0 is first mob/player)</li>
                    <li>Press <span className="key-box">TAB</span> to target yourself</li>
                </ul>
            </div>
        ),
        
        communication: (
            <div className="help-section">
                <h3>Communication</h3>
                <p>There are several ways to communicate with other players in the game.</p>
                
                <h4>Chat Channels</h4>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Command</th>
                            <th>Description</th>
                            <th>Example</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><span className="command-box">/g message</span></td>
                            <td>Global chat - Send message to all online players</td>
                            <td>/g Hello everyone!</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/w name message</span></td>
                            <td>Whisper - Send private message to a player</td>
                            <td>/w maven Hi there</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/r message</span></td>
                            <td>Reply - Respond to last whisper</td>
                            <td>/r Thanks!</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/p message</span></td>
                            <td>Party chat - Send message to party members</td>
                            <td>/p Need healing!</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/e emote</span></td>
                            <td>Emote - Show an action in the room</td>
                            <td>/e waves hello</td>
                        </tr>
                    </tbody>
                </table>
                
                <h4>Chat Tips</h4>
                <ul>
                    <li>Press <span className="key-box">C</span> to focus the chat input</li>
                    <li>Use <span className="key-box">Shift + ↑/↓</span> to scroll through chat history</li>
                    <li>The chat selector on the left lets you choose a default channel</li>
                    <li>Type <span className="command-box">/who</span> to see who is online</li>
                    <li>Type <span className="command-box">/who name</span> to see information about a specific player</li>
                </ul>
            </div>
        ),
        
        hotkeys: (
            <div className="help-section">
                <h3>Hotkeys & Macros</h3>
                <p>Hotkeys allow you to perform actions quickly using function keys (F1-F9).</p>
                
                <h4>Using Hotkeys</h4>
                <ul>
                    <li>Function keys F1-F9 can be used to perform custom actions</li>
                    <li>The action bar is located at the bottom left of the client</li>
                    <li>Click on a hotkey button or press the corresponding function key to use it</li>
                    <li>There are 4 sets of hotkeys you can switch between</li>
                </ul>
                
                <h4>Editing Hotkeys</h4>
                <ol>
                    <li>Right-click on any hotkey button to open the editor</li>
                    <li>In the first field, enter the name to display on the button</li>
                    <li>In the second field, enter the command to execute</li>
                    <li>Click Save to apply your changes</li>
                </ol>
                
                <h4>Hotkey Sets</h4>
                <p>You can switch between 4 different sets of hotkeys:</p>
                <ul>
                    <li>Click the number buttons (1-4) below the action bar</li>
                    <li>Use <span className="key-box">Shift + ←/→</span> to cycle between sets</li>
                </ul>
                
                <h4>Example Hotkeys</h4>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Command</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Attack</td>
                            <td>/a</td>
                            <td>Attack selected target</td>
                        </tr>
                        <tr>
                            <td>Atk 0</td>
                            <td>/a 0</td>
                            <td>Attack first target in room</td>
                        </tr>
                        <tr>
                            <td>Smash</td>
                            <td>/s smash</td>
                            <td>Use smash ability on selected target</td>
                        </tr>
                        <tr>
                            <td>FB</td>
                            <td>/cast fireball, 0</td>
                            <td>Cast fireball on first target</td>
                        </tr>
                        <tr>
                            <td>Heal</td>
                            <td>/cast heal wounds</td>
                            <td>Cast heal on selected target</td>
                        </tr>
                        <tr>
                            <td>HP Pot</td>
                            <td>/use potion of health</td>
                            <td>Use a health potion</td>
                        </tr>
                    </tbody>
                </table>
                
                <h4>Mouse Options</h4>
                <p>You can also set up mouse options that appear in the context menu when right-clicking on targets:</p>
                <ol>
                    <li>Open the hotkey editor by right-clicking any hotkey button</li>
                    <li>Click on the "Mouse" tab at the top</li>
                    <li>Configure commands for M1-M5 buttons</li>
                    <li>Use {'{target}'} in your commands to insert the target's name</li>
                </ol>
                
                <h4>Ctrl+Click Options</h4>
                <p>Configure actions for Ctrl+clicking on targets:</p>
                <ul>
                    <li>Ctrl+Left Click: Default is attack target</li>
                    <li>Ctrl+Right Click: Default is examine target</li>
                    <li>These can be customized in the Mouse tab of the hotkey editor</li>
                </ul>
            </div>
        ),
        
        inventory: (
            <div className="help-section">
                <h3>Inventory & Equipment</h3>
                <p>Manage your items and equipment in the game.</p>
                
                <h4>Inventory</h4>
                <ul>
                    <li>Open your inventory by clicking the "Inventory" button in the action bar</li>
                    <li>View your equipped items and available inventory space</li>
                    <li>Drag items to equip or move them</li>
                    <li>Right-click on an item to use it or drop it</li>
                    <li>Use <span className="command-box">/inv</span> to view your inventory</li>
                </ul>
                
                <h4>Equipment</h4>
                <ul>
                    <li>Your equipment is displayed in the inventory</li>
                    <li>You can equip or unequip items</li>
                    <li>Some items have specific slots (e.g., weapon, shield, armor)</li>
                    <li>Right-click on an item to equip/unequip it</li>
                    <li>Equipped items provide bonuses and are visible in your character panel</li>
                </ul>
                
                <h4>Item Types</h4>
                <p>Items can be categorized:</p>
                <ul>
                    <li><strong>Consumables</strong> - Useable items that provide temporary effects</li>
                    <li><strong>Equipment</strong> - Items that can be worn or wielded</li>
                    <li><strong>Materials</strong> - Resources used for crafting or trading</li>
                    <li><strong>Quest Items</strong> - Required for completing quests</li>
                </ul>
            </div>
        ),
        
        interface: (
            <div className="help-section">
                <h3>User Interface</h3>
                <p>Learn about the different parts of the game interface.</p>
                
                <h4>Main Interface Elements</h4>
                <ul>
                    <li><strong>Character Panel</strong> - Shows your character portrait and basic stats</li>
                    <li><strong>Health Bar</strong> - Displays your current and maximum health</li>
                    <li><strong>Mana Bar</strong> - Shows your current and maximum mana</li>
                    <li><strong>Stamina Bar</strong> - Indicates your stamina level</li>
                    <li><strong>User List</strong> - Shows players and mobs in the current room</li>
                    <li><strong>Map Grid</strong> - Displays a visual representation of the area</li>
                    <li><strong>Room Info</strong> - Shows information about the current room</li>
                    <li><strong>Chat Console</strong> - Displays game messages and chat</li>
                    <li><strong>Chat Input</strong> - Where you type commands and messages</li>
                    <li><strong>Action Bar</strong> - Contains your hotkey buttons</li>
                </ul>
                
                <h4>Interacting with the Interface</h4>
                <ul>
                    <li><strong>Left-click</strong> on a player or mob to select them</li>
                    <li><strong>Right-click</strong> on a player or mob to open the context menu</li>
                    <li><strong>Ctrl+click</strong> on a player or mob to perform quick actions</li>
                    <li><strong>Right-click</strong> on hotkey buttons to edit them</li>
                    <li>Click the numbers below the action bar to switch hotkey sets</li>
                </ul>
                
                <h4>Panels and Windows</h4>
                <ul>
                    <li><strong>Inventory</strong> - View and manage your items</li>
                    <li><strong>Character Info</strong> - View detailed character information</li>
                    <li><strong>Shop</strong> - Buy and sell items</li>
                    <li><strong>Trainer</strong> - Learn new abilities</li>
                    <li><strong>Vault</strong> - Store items securely</li>
                    <li><strong>Trade</strong> - Exchange items with other players</li>
                </ul>
                
                <h4>Modal Features</h4>
                <ul>
                    <li>All modals can be dragged by clicking and holding the header</li>
                    <li>Multiple modals can be open simultaneously</li>
                    <li>Modal positions are saved and restored when you reopen them</li>
                    <li>Click on a modal to bring it to the front</li>
                    <li>Press <span className="key-box">Escape</span> to close the topmost modal</li>
                    <li>Use keyboard shortcuts (see Keyboard Shortcuts section) to quickly toggle modals</li>
                </ul>
                
                <h4>Console Features</h4>
                <ul>
                    <li><strong>Second Console</strong> - Press <span className="key-box">Alt</span> + <span className="key-box">R</span> or <span className="key-box">Ctrl</span> + <span className="key-box">Shift</span> + <span className="key-box">R</span> to toggle a second console window</li>
                    <li><strong>Console Layout</strong> - Choose horizontal (side by side) or vertical (stacked) layout</li>
                    <li><strong>Context Menu</strong> - Right-click on the console area for quick access to settings</li>
                </ul>
                
                <h4>Console Context Menu Options</h4>
                <ul>
                    <li><strong>Second Console</strong> - Toggle the second console on/off</li>
                    <li><strong>Layout</strong> - Switch between horizontal and vertical layouts</li>
                    <li><strong>Swap Console Order</strong> - Switch which console appears first</li>
                    <li><strong>Show Timestamps</strong> - Display timestamps on messages</li>
                    <li><strong>Font Family</strong> - Choose from various monospace fonts</li>
                    <li><strong>Font Size</strong> - Adjust text size (Small, Medium, Large, Extra Large)</li>
                    <li><strong>Clear Console</strong> - Clear messages from console 1, 2, or both</li>
                </ul>
                
                <h4>Chat Channels</h4>
                <p>Use the chat selector to choose your default channel:</p>
                <ul>
                    <li><strong>Say</strong> - Talk to players in the same room</li>
                    <li><strong>Global</strong> - Talk to all players online</li>
                    <li><strong>Party</strong> - Talk to your party members</li>
                    <li><strong>Clan</strong> - Talk to your clan members</li>
                </ul>
            </div>
        ),
        
        commands: (
            <div className="help-section">
                <h3>Command Reference</h3>
                <p>Here's a comprehensive list of commands available in the game.</p>
                
                <h4>General Commands</h4>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Command</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><span className="command-box">/stats</span></td>
                            <td>View your character stats</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/skills</span></td>
                            <td>View your character skills</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/examine target</span></td>
                            <td>Get information about a player or mob</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/use item</span></td>
                            <td>Use an item from your inventory</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/get</span></td>
                            <td>Pick up items from the ground</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/give name amount gold</span></td>
                            <td>Give gold to another player</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/roll</span></td>
                            <td>Roll a random number</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/tnl</span></td>
                            <td>Show experience needed to level up</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/auc i</span></td>
                            <td>View detailed information about an auction item</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/auc info</span></td>
                            <td>View detailed information about an auction item</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/clear</span> or <span className="command-box">/cls</span></td>
                            <td>Clear both console windows</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/clear 1</span></td>
                            <td>Clear console 1 only</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/clear 2</span></td>
                            <td>Clear console 2 only</td>
                        </tr>
                    </tbody>
                </table>
                
                <h4>Party Commands</h4>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Command</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><span className="command-box">/party invite name</span></td>
                            <td>Invite a player to your party</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/party invite all</span></td>
                            <td>Invite all players in the room</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/party accept</span></td>
                            <td>Accept a party invitation</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/party quit</span></td>
                            <td>Leave your current party</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/party leader name</span></td>
                            <td>Change the party leader</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/party split</span></td>
                            <td>Share gold between party members</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/party info</span></td>
                            <td>Show information about your party</td>
                        </tr>
                    </tbody>
                </table>
                
                <h4>Bank/Vault Commands</h4>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Command</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><span className="command-box">/balance</span></td>
                            <td>Check your bank balance</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/deposit amount</span></td>
                            <td>Deposit gold into your bank</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/withdraw amount</span></td>
                            <td>Withdraw gold from your bank</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/vault list</span></td>
                            <td>View items in your vault</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/vault get number</span></td>
                            <td>Remove an item from your vault</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/vault upgrade</span></td>
                            <td>Increase your vault capacity</td>
                        </tr>
                    </tbody>
                </table>
                
                <h4>Trade Commands</h4>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Command</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><span className="command-box">/trade invite name</span></td>
                            <td>Invite a player to trade</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/trade accept</span></td>
                            <td>Accept a trade invitation</td>
                        </tr>
                        <tr>
                            <td><span className="command-box">/trade decline</span></td>
                            <td>Decline a trade invitation</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        ),
        
        keyboard: (
            <div className="help-section">
                <h3>Keyboard Shortcuts</h3>
                <p>These keyboard shortcuts can help you navigate and interact with the game more efficiently. For movement keys, see the Movement section.</p>
                
                <h4>Modal Shortcuts</h4>
                <p>These shortcuts toggle (open/close) various modals. Press the shortcut once to open, press again to close.</p>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Shortcut</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><span className="key-box">Ctrl</span> + <span className="key-box">I</span></td>
                            <td>Toggle Inventory modal</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Ctrl</span> + <span className="key-box">A</span></td>
                            <td>Toggle Abilities modal</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Ctrl</span> + <span className="key-box">S</span></td>
                            <td>Toggle Train Stats modal</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Ctrl</span> + <span className="key-box">H</span></td>
                            <td>Toggle Hotkey Editor modal</td>
                        </tr>
                    </tbody>
                </table>
                <p className="help-note">Note: These shortcuts do not work when typing in input fields. Press <span className="key-box">Escape</span> to close the topmost modal.</p>
                
                <h4>Chat & Interface Keys</h4>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Key</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><span className="key-box">C</span></td>
                            <td>Focus chat input</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Escape</span></td>
                            <td>Unfocus chat input / Close topmost modal</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Shift + ↑/↓</span></td>
                            <td>Scroll through chat history</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Shift + ←/→</span></td>
                            <td>Switch between hotkey sets</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Tab</span></td>
                            <td>Target yourself</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">F1</span> - <span className="key-box">F9</span></td>
                            <td>Activate hotkeys</td>
                        </tr>
                    </tbody>
                </table>
                
                <h4>Other Shortcuts</h4>
                <table className="help-table">
                    <thead>
                        <tr>
                            <th>Key</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><span className="key-box">Alt + Number</span></td>
                            <td>Select target by position</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Alt + A</span></td>
                            <td>Start attack</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Alt + S</span></td>
                            <td>Stop attack</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Alt + T</span> or <span className="key-box">Ctrl</span> + <span className="key-box">Shift</span> + <span className="key-box">T</span></td>
                            <td>Toggle timestamps</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Alt + R</span> or <span className="key-box">Ctrl</span> + <span className="key-box">Shift</span> + <span className="key-box">R</span></td>
                            <td>Toggle second console</td>
                        </tr>
                        <tr>
                            <td><span className="key-box">Ctrl + Click</span></td>
                            <td>Perform quick action on target</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        )
    };

    return (
        <div className="help-content">
            {content[activeSection]}
        </div>
    );
}

export default Help;